import { Button, DatedList, Icon } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as EmptyResultsSVG } from 'images/illustration/no-advertisers.svg'
import prepareFilterParams from 'utils/prepareFilterParams'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import formatRelative from 'utils/dateFns/formatRelative'
import EmptyResults from 'components/EmptyResults'
import Item from 'modules/Services/Account/subcomponents/TransactionsList/subcomponents/TransactionRow'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link } from 'react-router-dom'
import styles from 'modules/Services/Account/subcomponents/TransactionsList/transactions-list.module.scss'
import { useGetAccountAction } from 'modules/Services/hooks/useGetAccountAction'
import { ACCOUNT_TRANSFERS_CLEAN, FETCH_ACCOUNT_TRANSFERS } from 'modules/Services/types'

const TransactionList = ({ networkId, accountId, isDepositButton = false }) => {
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation(['services'])
  const dispatch = useDispatch()
  const { cancelableDispatch } = useCancelToken()
  const depositConfig = useGetAccountAction('deposit')
  const { i18n } = useSelector(({ app }) => app)
  const [page, setPage] = useState(1)

  const {
    filters,
    transfers: { loading, loaded, loadFailed, data = [], pagination },
  } = useSelector((state) => state['services/network-account'])

  const {
    query = '',
    status = [],
    date_from = '',
    date_to = '',
  } = useMemo(() => filters || {}, [filters])

  useEffect(() => {
    dispatch({ type: ACCOUNT_TRANSFERS_CLEAN })
    setPage(1)
  }, [filters])

  useEffect(() => {
    !!networkId &&
      cancelableDispatch({
        type: FETCH_ACCOUNT_TRANSFERS,
        payload: {
          networkId,
          accountId,
          params: prepareFilterParams({
            query: query,
            offset: (page - 1) * pagination.limit,
            limit: pagination.limit,
            status: status,
            date_from: date_from,
            date_to: date_to,
          }),
        },
      })
  }, [filters, networkId, page])

  const transactions = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        date: item.created_at,
      })),
    [data],
  )

  useEffect(() => {
    return () => dispatch({ type: ACCOUNT_TRANSFERS_CLEAN })
  }, [])

  return (
    <DatedList
      emptyComponent={
        <EmptyResults
          className={styles['transfers__empty-list']}
          image={<EmptyResultsSVG />}
          header={t('transfers_list.empty_list_header')}
          text={t('transfers_list.empty_list_label')}
          actionButton={
            isDepositButton ? (
              <Button
                startIcon={<Icon name={'card'} size={'l'} />}
                className={styles['transfers__empty-list-button']}
                size="l"
                rounded
                variant="primary"
                type="button"
                component={Link}
                to={pathGen(depositConfig.url, {
                  i18n,
                  accountId,
                })}
              >
                {t('button.fund')}
              </Button>
            ) : null
          }
        />
      }
      itemComponent={Item}
      pagination={pagination}
      className={styles['transfers__list']}
      items={transactions}
      moreButtonLabel={t('wallets::transaction.showMore')}
      handleGetMoreItems={() => setPage(page + 1)}
      loading={loading}
      loaded={loaded}
      loadFailed={loadFailed}
      l10n={{
        formatFunction: formatRelative,
        today: t('common::dated_list.date_format.today'),
        yesterday: t('common::dated_list.date_format.yesterday'),
        formatDates: {
          short: t('common::dated_list.date_format.short'),
          long: t('common::dated_list.date_format.long'),
        },
      }}
    />
  )
}

TransactionList.propTypes = {
  networkId: PropTypes.string,
  networkType: PropTypes.string,
  accountId: PropTypes.string,
  isDepositButton: PropTypes.bool,
}

export default TransactionList
